// Lógica del registro (paso 1: usuario + Google, paso 2: empresa opcional).
// Reutiliza el mismo patrón de persistencia de sesión que el login
// (pages/login/functions.ts authenticateUser).
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signOut,
  GoogleAuthProvider,
} from "firebase/auth";
import type { UserCredential } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import i18n from "i18next";
import { auth, googleProvider } from "../../firebaseConfig";
import { useUserStore } from "../../store/userStore";
import { setLoggingOut } from "../../utils/tokenUtils";
import api from "../../utils/api";
import { isCompanySuspendedError } from "../../utils/apiErrorCodes";
import { clearDemoToken, getDemoToken } from "../../utils/demoInvite";
import { clearAttribution, getAttribution, getPrefill } from "../../utils/attribution";
import { normalizeRfc } from "../../utils/rfc";
import { parseUseCase } from "../../utils/useCases";
import { clearInviteToken, getInviteToken } from "../../utils/teamInvite";
import type { RegisterUserForm, RegisterCompanyForm } from "./types";
import { checkoutDestination } from "./checkoutDestination";
import { trackLoginSucceeded, type AuthMethod } from "../../analytics/authAnalytics";

const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;

/**
 * Error que transporta una clave de i18n del namespace "register".
 * El componente la traduce con t(message).
 */
export class RegisterError extends Error {}

/** `meta.code` de un error de la API (identificador estable, no pasa por i18n). */
const apiErrorCode = (err: unknown): string | undefined =>
  (err as { response?: { data?: { meta?: { code?: string } } } })?.response?.data?.meta?.code;

/** Códigos con los que /auth/register rechaza una invitación → clave i18n de "register". */
const INVITE_ERROR_KEYS: Record<string, string> = {
  INVITE_INVALID: "errorInviteInvalid",
  INVITE_EMAIL_MISMATCH: "errorInviteEmailMismatch",
  INVITE_OTHER_COMPANY: "errorInviteOtherCompany",
  INVITE_NOT_ELIGIBLE: "errorInviteNotEligible",
};

const mapFirebaseError = (err: unknown): string => {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/email-already-in-use":
        return "errorEmailInUse";
      case "auth/weak-password":
        return "errorWeakPassword";
      case "auth/invalid-email":
        return "errorInvalidEmail";
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
      case "auth/user-cancelled":
        return "errorPopupClosed";
      case "auth/too-many-requests":
        return "errorRateLimited";
      default:
        return "errorRegister";
    }
  }
  return "errorRegister";
};

// Persiste token/sesión igual que el login para dejar al usuario autenticado.
// `data` es el payload devuelto por /auth/register (mismo formato que /login).
const persistSession = (data: Record<string, unknown>, method: AuthMethod) => {
  const { setUser } = useUserStore.getState();
  setLoggingOut(false);

  const token = data.token as string | undefined;
  const sessionId = data.sessionId as string | undefined;
  const userId = (data.userId || data.id) as string | undefined;
  const roleId = data.role as string | undefined;
  const companyId = data.companyId as string | undefined;

  if (token) localStorage.setItem("token", token);
  if (sessionId) localStorage.setItem("sessionId", sessionId);
  if (userId) localStorage.setItem("userId", userId);
  if (roleId) {
    localStorage.setItem("userRoleId", roleId);
    // El alta self-service siempre da el rol "admin"; quien entra por una
    // invitación al equipo trae el nombre de su rol (`roleName`, CC-CONSOLE#1512).
    localStorage.setItem(
      "userRole",
      typeof data.roleName === "string" && data.roleName ? data.roleName : "admin",
    );
  }
  if (companyId) {
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("userCompanyId", companyId);
  }

  setUser(data);
  // También es una entrada: Google (cuenta nueva o existente) y el alta abren
  // sesión por aquí y no por `authenticateUser`. Sin esto, el «último acceso»
  // de la ficha de Comportamiento sólo vería a quien entra con contraseña.
  trackLoginSucceeded(method);
};

/** Datos extra del alta manual (CC-CONSOLE#1550). */
export interface RegisterExtras {
  /** WhatsApp opcional, ya en E.164 (`+52…`). */
  phone?: string;
}

const sendRegister = async (
  idToken: string,
  firstName: string,
  lastName: string,
  method: AuthMethod,
  photoURL?: string,
  extras: RegisterExtras = {},
) => {
  let data: Record<string, unknown>;
  // Invitación a una demo (`/register?demo=<token>`): el backend mete al
  // usuario en la compañía de su demo y responde con `companyId`.
  const demoToken = getDemoToken();
  // Invitación al equipo (`/register?invite=<token>`, CC-CONSOLE#1512): el
  // usuario nace dentro de la compañía que lo invitó (`invitedCompanyId`).
  const inviteToken = getInviteToken();
  // Procedencia (funnel v2, CC-CONSOLE#1520): de qué canal llegó. El backend
  // sólo la guarda en un alta nueva.
  const attribution = getAttribution();
  try {
    const res = await api.post(`${apiUrl}/auth/register`, {
      idToken,
      firstName,
      lastName,
      photoURL,
      platform: "Web",
      // Idioma del correo con el código de verificación del alta.
      lang: (i18n.language || "es").startsWith("en") ? "en" : "es",
      ...(extras.phone && !inviteToken && !demoToken ? { phone: extras.phone } : {}),
      ...(demoToken ? { demoToken } : {}),
      ...(inviteToken ? { inviteToken } : {}),
      ...(attribution ? { attribution } : {}),
    });
    data = (res.data?.data ?? res.data) as Record<string, unknown>;
    // De un solo uso: reclamada la demo, el token ya no sirve. Y si la cuenta
    // ya tiene compañía (la suya de siempre, no la de la demo) tampoco: el
    // backend nunca muda a alguien con compañía, así que reenviarlo en cada
    // entrada sólo pasearía una credencial.
    if (data.demoAccountId || data.companyId) clearDemoToken();
    // Igual la invitación al equipo: aceptada (o ya con compañía), no se reenvía.
    if (data.invitedCompanyId || data.companyId) clearInviteToken();
  } catch (err) {
    // Invitación al equipo que no se pudo aceptar: el backend no creó nada. Se
    // suelta la liga (reintentarla no cambia nada) y no queda sesión abierta.
    const inviteError = INVITE_ERROR_KEYS[apiErrorCode(err) ?? ""];
    if (inviteError) {
      clearInviteToken();
      await signOut(auth).catch(() => undefined);
      throw new RegisterError(inviteError);
    }
    // Compañía suspendida: el backend aplica la misma regla que /login. La
    // señal estable es `meta.code === "COMPANY_SUSPENDED"` (el mensaje viaja
    // traducido); la comparación por texto queda como respaldo. No dejamos
    // sesión abierta.
    const resp = (err as { response?: { status?: number; data?: { meta?: { message?: string } } } })
      .response;
    if (
      isCompanySuspendedError(err) ||
      (resp?.status === 403 && resp?.data?.meta?.message === "companySuspended")
    ) {
      await signOut(auth).catch(() => undefined);
      throw new RegisterError("errorCompanySuspended");
    }
    // Freno de abuso del alta (REGISTER_RATE_LIMITED): no se abre sesión.
    if (resp?.status === 429) {
      await signOut(auth).catch(() => undefined);
      throw new RegisterError("errorRateLimited");
    }
    throw err;
  }

  // Sesión única activa: igual que /login, no iniciamos sesión si ya hay otra
  // activa; el usuario debe cerrarla antes (desde la pantalla de login).
  if (data.hasActiveSession === true) {
    await signOut(auth).catch(() => undefined);
    throw new RegisterError("errorActiveSession");
  }

  persistSession(data, method);
  // Datos completos: incluyen userCreated / companyId / passwordRemoved, con los que
  // el front decide a dónde sigue la persona (ver googleFlow.ts).
  return data;
};

/** Paso 1 — registro con correo y contraseña. */
export const registerWithEmail = async (form: RegisterUserForm, extras: RegisterExtras = {}) => {
  const email = form.email.toLowerCase().trim();
  // Campo trampa (types.ts): se corta AQUÍ, antes de crear nada en Firebase,
  // para no dejar una cuenta a medias. El backend lo repite para quien le
  // pegue directo al API.
  if (form.website?.trim()) throw new RegisterError("errorRegister");
  try {
    let idToken: string;
    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        email,
        form.password,
      );
      idToken = await cred.user.getIdToken();
    } catch (err) {
      // Recuperación del borde de fallo: si un intento previo creó el usuario en
      // Firebase pero /auth/register falló, el reintento choca con
      // auth/email-already-in-use. Si la contraseña coincide (mismo dueño),
      // iniciamos sesión y completamos el registro (idempotente en backend).
      if (
        err instanceof FirebaseError &&
        err.code === "auth/email-already-in-use"
      ) {
        try {
          const cred = await signInWithEmailAndPassword(
            auth,
            email,
            form.password,
          );
          idToken = await cred.user.getIdToken();
        } catch {
          // La contraseña no coincide: el correo pertenece a otra cuenta.
          throw new RegisterError("errorEmailInUse");
        }
      } else {
        throw err;
      }
    }
    return await sendRegister(idToken, form.firstName, form.lastName, "password", undefined, extras);
  } catch (err) {
    if (err instanceof RegisterError) throw err;
    if (err instanceof FirebaseError) throw new RegisterError(mapFirebaseError(err));
    throw new RegisterError("errorRegister");
  }
};

/**
 * Continuar con Google (sirve tanto para registro como para login).
 *
 * Caso especial: si el correo ya existe en una cuenta creada manualmente
 * (email/contraseña), Firebase rechaza el popup con
 * `auth/account-exists-with-different-credential`. En ese caso enlazamos Google
 * a esa cuenta en el backend (sin pedir la contraseña, confiando en el correo
 * verificado por Google) y reintentamos el inicio con la credencial pendiente.
 * El usuario queda autenticado con Google sobre su cuenta de siempre.
 */
export const signInOrRegisterWithGoogle = async () => {
  try {
    let cred: UserCredential;
    try {
      cred = await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (
        err instanceof FirebaseError &&
        err.code === "auth/account-exists-with-different-credential"
      ) {
        const pendingCred = GoogleAuthProvider.credentialFromError(err);
        const googleIdToken = pendingCred?.idToken;
        if (!pendingCred || !googleIdToken) throw err;

        // Enlazar Google a la cuenta existente en el backend.
        try {
          await api.post(`${apiUrl}/auth/link-google`, { googleIdToken });
        } catch (linkErr) {
          const status = (linkErr as { response?: { status?: number } })?.response?.status;
          throw new RegisterError(status === 429 ? "errorRateLimited" : "errorGoogleLink");
        }

        // Reintentar el inicio: primero con la credencial pendiente (sin abrir
        // otro popup); si ya fue consumida, reabrimos el popup de Google.
        try {
          cred = await signInWithCredential(auth, pendingCred);
        } catch {
          cred = await signInWithPopup(auth, googleProvider);
        }
      } else {
        throw err;
      }
    }

    const idToken = await cred.user.getIdToken();
    const display = (cred.user.displayName || "").trim();
    const [firstName, ...rest] = display.split(" ");
    const lastName = rest.join(" ");
    const photoURL = cred.user.photoURL || undefined;
    return await sendRegister(idToken, firstName || "", lastName || "", "google", photoURL);
  } catch (err) {
    if (err instanceof RegisterError) throw err;
    if (err instanceof FirebaseError) throw new RegisterError(mapFirebaseError(err));
    throw new RegisterError("errorGoogle");
  }
};

/** Alias retro-compatible usado por la vista de registro. */
export const registerWithGoogle = signInOrRegisterWithGoogle;

/**
 * Paso 2 — crear la empresa y vincularla al usuario del paso 1.
 *
 * Con `personal: true` la persona eligió no capturar una empresa: el backend le
 * asigna una con su propio nombre y aquí no viaja ningún dato del formulario.
 */
export const createCompany = async (
  form: RegisterCompanyForm,
  {
    personal = false,
    plan,
    useCase,
  }: {
    personal?: boolean;
    /**
     * Slug del plan con el que llegó (`/register?plan=`). Uno de pago evita que
     * el API active la licencia gratis por omisión (CC-CONSOLE#1550).
     */
    plan?: string;
    /** Caso de uso elegido (CC-CONSOLE #1614); sin él, el de la landing si vino. */
    useCase?: string | null;
  } = {},
) => {
  // Procedencia e intención (funnel v2): la empresa las hereda para el CRM y
  // para el aviso de licencia activada. Van también en la cuenta «sin empresa».
  const attribution = getAttribution();
  const intent = getPrefill()?.intent;
  const chosenUseCase = parseUseCase(useCase) ?? parseUseCase(getPrefill()?.use_case);
  const origin = {
    ...(attribution ? { attribution } : {}),
    ...(intent ? { intent } : {}),
    ...(plan ? { plan } : {}),
    ...(chosenUseCase ? { useCase: chosenUseCase } : {}),
  };
  try {
    const res = await api.post(
      `${apiUrl}/auth/register/company`,
      personal
        ? { personal: true, ...origin }
        : {
            name: form.name,
            business_name: form.business_name || undefined,
            rfc: form.rfc ? normalizeRfc(form.rfc) : undefined,
            email: form.email || undefined,
            phone: form.phone || undefined,
            website: form.website || undefined,
            ...origin,
          },
    );
    // Ya quedó en la empresa: no se reenvía ni se prellena otra vez.
    clearAttribution();

    // Tolerante a la forma de la respuesta (envoltura `data` anidada o plana).
    const root = (res.data?.data ?? res.data ?? {}) as Record<string, any>;
    const companyId =
      root.company_id ||
      root.data?.company_id ||
      root.company?.id ||
      root.data?.company?.id;

    if (companyId) {
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userCompanyId", companyId);
    }
    return root;
  } catch (err) {
    // Ya tenía empresa (p. ej. doble envío de este paso): no es un error del
    // alta, se sigue con la que ya existe.
    const resp = (err as { response?: { status?: number; data?: { meta?: { code?: string } } } })?.response;
    const code = resp?.data?.meta?.code;
    if (code === "COMPANY_ALREADY_REGISTERED") {
      clearAttribution();
      return { alreadyRegistered: true };
    }
    if (code === "INVALID_RFC") throw new RegisterError("invalidRfc");
    if (resp?.status === 429) throw new RegisterError("errorRateLimited");
    throw new RegisterError("errorCompany");
  }
};

/**
 * Paso 3 (sólo cuando el alta viene con un plan elegido) — arranca el Checkout
 * de la licencia y devuelve la URL a la que hay que mandar al usuario: la de
 * Stripe si hay algo que cobrar, o la entrada de la app si la licencia es
 * gratuita (el servidor la activa sin pago).
 *
 * El cobro va SIEMPRE a la cuenta del reseller: es lo que el tenant paga por
 * usar la plataforma, no dinero de sus clientes finales. Esa decisión la toma el
 * backend a partir de `type: "license"`.
 */
export const startLicenseCheckout = async (
  licenseId: string,
  companyId: string,
  // Usuarios contratados: en un plan por unidad con precio por volumen, Stripe
  // aplica solo el escalón que corresponde (desde 5, TODOS a la tarifa baja).
  quantity: number = 1,
  // "yearly" cobra el precio anual de la licencia con el cupón global del
  // pago anual; por omisión, el ciclo propio de la licencia.
  billing: "monthly" | "yearly" = "monthly",
): Promise<string> => {
  const origin = window.location.origin;
  try {
    const res = await api.post(`${apiUrl}/subscriptions/checkout`, {
      companyId,
      type: "license",
      licenseId,
      quantity: Math.max(1, Math.floor(quantity || 1)),
      billing,
      successUrl: `${origin}/purchase/success`,
      cancelUrl: `${origin}/purchase/cancel`,
    });
    // Stripe si hay algo que cobrar; la entrada de la app si la licencia es
    // gratuita (el servidor ya la activó). Ver checkoutDestination.ts.
    const url = checkoutDestination(res.data, origin);
    if (!url) throw new RegisterError("errorCheckout");
    return url;
  } catch (err) {
    if (err instanceof RegisterError) throw err;
    throw new RegisterError("errorCheckout");
  }
};

/** `freeLicense` de la respuesta de `/auth/register/company` (CC-CONSOLE#1550). */
export interface FreeLicenseResult {
  activated: boolean;
  licenseId?: string;
  slug?: string | null;
  reason?: string;
}

/** Saca `freeLicense` de la respuesta de `createCompany` (tolerante a la envoltura). */
export const freeLicenseOf = (root: unknown): FreeLicenseResult | null => {
  const r = (root ?? {}) as { freeLicense?: unknown; data?: { freeLicense?: unknown } };
  const value = (r.data?.freeLicense ?? r.freeLicense) as FreeLicenseResult | undefined;
  return value && typeof value.activated === "boolean" ? value : null;
};
